import {
  Building2,
  Kanban,
  LayoutDashboard,
  ListTodo,
  PanelLeftClose,
  Settings,
  Users,
} from "lucide-react";
import { CanAccess, useStore, useTranslate } from "ra-core";
import { Link, useLocation } from "react-router";
import { Button } from "@/components/ui/button";
import { cn } from "@/lib/utils";
import { useConfigurationContext } from "../root/ConfigurationContext";
import { SIDEBAR_COLLAPSED_STORE_KEY } from "./sidebarStore";

interface NavItem {
  to: string;
  labelKey: string;
  fallback: string;
  icon: typeof LayoutDashboard;
  match: string;
}

const WORKSPACE_ITEMS: NavItem[] = [
  {
    to: "/",
    labelKey: "crm.nav.home",
    fallback: "Métricas",
    icon: LayoutDashboard,
    match: "/",
  },
  {
    to: "/contacts",
    labelKey: "crm.nav.contacts",
    fallback: "Contactos",
    icon: Users,
    match: "/contacts",
  },
  {
    to: "/companies",
    labelKey: "crm.nav.companies",
    fallback: "Empresas",
    icon: Building2,
    match: "/companies",
  },
  {
    to: "/deals",
    labelKey: "crm.nav.pipeline",
    fallback: "Pipeline",
    icon: Kanban,
    match: "/deals",
  },
  {
    to: "/tasks",
    labelKey: "crm.nav.activities",
    fallback: "Actividades",
    icon: ListTodo,
    match: "/tasks",
  },
];

function isActivePath(pathname: string, match: string): boolean {
  if (match === "/") {
    return pathname === "/";
  }
  return pathname === match || pathname.startsWith(`${match}/`);
}

export function AppSidebar() {
  const { darkModeLogo, title } = useConfigurationContext();
  const location = useLocation();
  const translate = useTranslate();
  const [isSidebarCollapsed, setIsSidebarCollapsed] = useStore<boolean>(
    SIDEBAR_COLLAPSED_STORE_KEY,
    false,
  );

  if (isSidebarCollapsed) {
    return null;
  }

  return (
    <aside className="hidden md:flex h-screen w-60 shrink-0 flex-col border-r border-sidebar-border bg-sidebar text-sidebar-foreground">
      <div className="flex items-start justify-between gap-2 px-3 pt-4">
        <Link
          to="/"
          className="flex min-w-0 items-center gap-3 px-2 py-2 no-underline text-sidebar-foreground"
        >
          <img src={darkModeLogo} alt="" className="h-9 w-9 object-contain" />
          <div className="flex flex-col leading-tight">
            <span className="text-sm font-semibold tracking-wide">{title}</span>
            <span className="text-[11px] text-muted-foreground">Bogotá</span>
          </div>
        </Link>
        <Button
          type="button"
          variant="ghost"
          size="icon"
          className="mt-2 text-sidebar-foreground/80 hover:text-sidebar-foreground"
          aria-label={translate("crm.nav.collapse_sidebar", {
            _: "Ocultar menú",
          })}
          onClick={() => setIsSidebarCollapsed(true)}
        >
          <PanelLeftClose className="h-4 w-4" aria-hidden="true" />
        </Button>
      </div>

      <p className="px-5 pb-2 text-[10px] font-medium uppercase tracking-[0.16em] text-muted-foreground">
        {translate("crm.nav.workspace", { _: "Workspace" })}
      </p>
      <nav
        className="flex min-h-0 flex-1 flex-col gap-0.5 overflow-y-auto px-3"
        aria-label="Principal"
      >
        {WORKSPACE_ITEMS.map((item) => {
          const Icon = item.icon;
          const active = isActivePath(location.pathname, item.match);
          return (
            <Link
              key={item.to}
              to={item.to}
              className={cn(
                "flex items-center gap-3 rounded-lg px-3 py-2 text-sm no-underline transition-colors",
                active
                  ? "bg-sidebar-accent text-sidebar-accent-foreground"
                  : "text-sidebar-foreground/75 hover:bg-sidebar-accent/60 hover:text-sidebar-foreground",
              )}
            >
              <Icon className="h-4 w-4 shrink-0" aria-hidden="true" />
              {translate(item.labelKey, { _: item.fallback })}
            </Link>
          );
        })}
      </nav>

      <div className="mt-auto border-t border-sidebar-border px-3 py-4">
        <p className="px-2 pb-2 text-[10px] font-medium uppercase tracking-[0.16em] text-muted-foreground">
          {translate("crm.nav.account", { _: "Cuenta" })}
        </p>
        <CanAccess resource="configuration" action="edit" error={null}>
          <Link
            to="/settings"
            className={cn(
              "flex items-center gap-3 rounded-lg px-3 py-2 text-sm no-underline transition-colors",
              location.pathname.startsWith("/settings")
                ? "bg-sidebar-accent text-sidebar-accent-foreground"
                : "text-sidebar-foreground/75 hover:bg-sidebar-accent/60",
            )}
          >
            <Settings className="h-4 w-4" aria-hidden="true" />
            {translate("crm.settings.title")}
          </Link>
        </CanAccess>
      </div>
    </aside>
  );
}
